import React, { useEffect, useMemo, useState } from "react";
import { CircularProgress } from "@mui/material";
import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";
import { Match } from "../../types/Match";
import { KnockoutStage } from "../../types/TournamentMatch";
import { useBets } from "../../common/contexts/betContext";
import { getKnockoutMatches } from "../../services/matchService";
import BetModal from "../components/BetModal";
import { mockMatch } from "../MobileBets/MobileBetsBody";
import FixturesPlaceholder from "../MobileFixtures/FixturesPlaceholder";
import StagePointsChip from "../../common/StagePointsChip";
// Shared with the desktop bracket so both platforms render the same data/logic
// and card. The mobile layout is a single scrollable column of rounds rather
// than the two-sided poster, which doesn't fit a phone width.
import BracketMatchCard from "../../Browser/BrowserKnockout/BracketMatchCard";
import {
  bracketHasMatches,
  getWinner,
  isTbd,
  isTbdMatch,
  matchesInStage,
} from "../../Browser/BrowserKnockout/bracketLayout";
import "../../Browser/BrowserKnockout/BrowserKnockout.scss";
import "./MobileKnockout.scss";

// Round pills, outer round → Final. `short` keeps the bar compact on narrow
// phones; `label` is the full round title shown above the list.
const STAGE_TABS: { key: KnockoutStage; short: string; label: string }[] = [
  { key: "Round of 32", short: "R32", label: "Round of 32" },
  { key: "Round of 16", short: "R16", label: "Round of 16" },
  { key: "Quarter-finals", short: "QF", label: "Quarter-finals" },
  { key: "Semi-finals", short: "SF", label: "Semi-finals" },
  { key: "Play-off for third place", short: "3rd", label: "Third place" },
  { key: "Final", short: "Final", label: "Final" },
];

function variantForStage(stage: KnockoutStage): "default" | "final" | "third" {
  if (stage === "Final") return "final";
  if (stage === "Play-off for third place") return "third";
  return "default";
}

const MobileKnockout: React.FC = () => {
  const { bets } = useBets();
  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);

  // The live bracket only contains ties whose teams are decided; rounds fill in
  // as the tournament progresses.
  useEffect(() => {
    let active = true;
    getKnockoutMatches()
      .then((data) => {
        if (active) setMatches(data);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  // Group by the reliable RoundNumber; only rounds that actually have ties get a
  // pill (so undecided/future rounds simply don't appear).
  const byStage = useMemo(() => {
    const map = new Map<KnockoutStage, Match[]>();
    for (const tab of STAGE_TABS) {
      map.set(tab.key, matchesInStage(matches, tab.key));
    }
    return map;
  }, [matches]);

  const tabs = useMemo(
    () => STAGE_TABS.filter((t) => (byStage.get(t.key)?.length ?? 0) > 0),
    [byStage]
  );

  // Land on the first round the user can still act on (teams known, not yet
  // played); otherwise the first populated round. Derived from the loaded data,
  // so it resolves once the fetch returns.
  const initialStage = useMemo<KnockoutStage>(() => {
    const actionable = tabs.find((t) =>
      (byStage.get(t.key) ?? []).some((m) => !m.wasPlayed && !isTbdMatch(m))
    );
    return (actionable ?? tabs[0])?.key ?? "Round of 32";
  }, [tabs, byStage]);

  // null until the user picks a round; the default landing round is used
  // meanwhile (kept separate so it can follow the async-loaded data).
  const [selectedStage, setSelectedStage] = useState<KnockoutStage | null>(null);
  const stage = selectedStage ?? initialStage;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentMatch, setCurrentMatch] = useState<Match>(mockMatch);

  const betFor = (match: Match) => bets.find((b) => b.matchId === match.id);
  const handleSelect = (match: Match) => {
    setCurrentMatch(match);
    setIsModalOpen(true);
  };

  if (loading) {
    return (
      <div className="mobileKnockout mobileKnockout--loading">
        <CircularProgress />
      </div>
    );
  }

  // `tabs` can be empty even when knockout matches exist if none fall in a known
  // round — guard so the `activeTab` lookup below can't dereference undefined.
  if (!bracketHasMatches(matches) || tabs.length === 0) {
    return (
      <div className="mobileKnockout">
        <FixturesPlaceholder message="No knockout matches yet — the bracket is set once the group stage ends." />
      </div>
    );
  }

  const activeTab = tabs.find((t) => t.key === stage) ?? tabs[0];
  const roundMatches = byStage.get(activeTab.key) ?? [];

  // Trophy banner once the Final is decided (hidden while it's still TBD).
  const final = byStage.get("Final")?.[0];
  const winner = final ? getWinner(final) : null;
  const championTeam =
    winner === "home"
      ? final?.homeTeam
      : winner === "away"
      ? final?.awayTeam
      : null;
  const showChampion =
    activeTab.key === "Final" && championTeam && !isTbd(championTeam);

  return (
    <div className="mobileKnockout">
      <div className="mobileKnockout__tabs groupPanelScroll" role="tablist">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={stage === tab.key}
            className={`mobileKnockout__tab${
              stage === tab.key ? " is-active" : ""
            }`}
            onClick={() => setSelectedStage(tab.key)}
          >
            {tab.short}
          </button>
        ))}
      </div>

      <div className="mobileKnockout__body">
        <h3 className="mobileKnockout__title">{activeTab.label}</h3>

        <div className="mobileKnockout__points">
          <StagePointsChip stage={activeTab.key} />
        </div>

        {showChampion && (
          <div className="mobileKnockout__champion">
            <EmojiEventsRoundedIcon className="mobileKnockout__trophy" />
            <span className="mobileKnockout__championName">
              {championTeam!.internationalName}
            </span>
            <span className="mobileKnockout__championLabel">Champion</span>
          </div>
        )}

        <div className="mobileKnockout__list">
          {roundMatches.map((match) => (
            <BracketMatchCard
              key={match.id}
              match={match}
              bet={betFor(match)}
              variant={variantForStage(activeTab.key)}
              onSelect={handleSelect}
            />
          ))}
        </div>
      </div>

      <BetModal
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        match={currentMatch}
      />
    </div>
  );
};

export default MobileKnockout;
